import { DateTime } from "luxon";
import { TMainTrackTimeFilter, TSelectedVariants, TTrackTypes } from "../types";

export const RED_PULSE_CLASSES = `tw-border-2 tw-border-[#FF7777] tw-shadow-[0px_0px_25px_rgba(255,119,119,0.75)]`;

export function getOrdinal(number: number) {
  // using the % modulo operator to get the last digit of the number
  const lastDigitOfNumber = ![11, 12, 13].includes(number) ? number % 10 : number;

  switch (lastDigitOfNumber) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

export function formatDateWithSuffix(date: Date) {
  const day = date.getDate();
  const suffix = (day: number) => {
    if (day >= 11 && day <= 13) {
      return "th";
    }
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const dayWithSuffix = day + suffix(day);
  const formattedDate = DateTime.fromJSDate(date).toFormat(`ccc, '${dayWithSuffix}' LLLL yyyy`);
  return formattedDate;
}

export const sortBySku = (variants: TSelectedVariants[], gameType: TTrackTypes) => {
  return variants.slice().sort((a: TSelectedVariants, b: TSelectedVariants) => {
    if (gameType === "MAIN_TRACK") {
      if (a.variantsSelectedObject.sku && b.variantsSelectedObject.sku) {
        const skuA = parseInt(a.variantsSelectedObject.sku.replace(".R", ""), 10);
        const skuB = parseInt(b.variantsSelectedObject.sku.replace(".R", ""), 10);
        return skuA - skuB;
      }
    }
    return 0;
  });
};

export const hasDifferentVariantSelectedTime = (variants: TSelectedVariants[], latestVariant: TSelectedVariants) => {
  return variants.some(
    (xFilterItem: TSelectedVariants) => !DateTime.fromISO(xFilterItem.variantSelectedTime.toISOString()).equals(DateTime.fromISO(latestVariant.variantSelectedTime.toISOString()))
  );
};

export const handleHourFromTitle = (title: string) => {
  const titleSplit = title.includes("/") ? title.split("/") : title;
  const hour = Array.isArray(titleSplit) ? titleSplit[2] : titleSplit;

  return hour;
};

export const handleSpaceBeforeAMOrPM = (timeString: string) => {
  const regex = /(PM|AM)$/i;

  return timeString.replace(regex, " $1");
};

export const getRaceHourFromSku = (sku: string | undefined) => {
  // SKUs in format YYMMDDHHMM
  if (!sku) {
    return 0;
  }

  let raceHour;
  if (sku[0] === "0") {
    raceHour = sku.slice(7, 9);
  } else {
    raceHour = sku.slice(6, 8);
  }

  const ret = parseInt(raceHour);
  return ret;
};

export const getRaceMinutesFromSku = (sku: string | undefined): number => {
  if (!sku) {
    return 0;
  }

  if (sku[0] === "0") {
    const raceHour = parseInt(sku.slice(7, 9));
    const raceMinutes = sku.slice(9, 11);
    return raceHour * 60 + parseInt(raceMinutes);
  }

  const raceHour = parseInt(sku.slice(6, 8));
  const raceMinutes = sku.slice(8, 10);
  return raceHour * 60 + parseInt(raceMinutes);
};

export const getStartTimeAndEndTime = (timeFilter: TMainTrackTimeFilter | "AFTERNOON" | "WHOLE_DAY" | "LUNCH" | "ARVO", track: TTrackTypes) => {
  const getMainTrackTimes = () => {
    switch (timeFilter) {
      case "MORNING":
        return { startHour: 7, endHour: 11 };
      case "LUNCH":
        return { startHour: 11, endHour: 15 };
      case "ARVO":
        return { startHour: 15, endHour: 19 };
      case "EVENING":
        return { startHour: 19, endHour: 23 };
      case "LATE":
        return { startHour: 23, endHour: 25 };
      case "WHOLE_DAY":
        return { startHour: 7, endHour: 25 };
      default:
        return { startHour: 7, endHour: 25 };
    }
  };

  const getJuniorTrackTimes = () => {
    switch (timeFilter) {
      case "MORNING":
        return { startHour: 7, endHour: 11 };
      case "LUNCH":
        return { startHour: 11, endHour: 15 };
      case "ARVO":
        return { startHour: 15, endHour: 19 };
      case "EVENING":
        return { startHour: 19, endHour: 23 };
      case "LATE":
        return { startHour: 23, endHour: 25 };
      case "WHOLE_DAY":
        return { startHour: 7, endHour: 25 };
      default:
        return { startHour: 7, endHour: 25 };
    }
  };

  const getMiniTrackTimes = () => {
    switch (timeFilter) {
      case "MORNING":
        return { startHour: 7, endHour: 11 };
      case "LUNCH":
        return { startHour: 11, endHour: 15 };
      case "ARVO":
        return { startHour: 15, endHour: 19 };
      case "EVENING":
        return { startHour: 19, endHour: 23 };
      case "LATE":
        return { startHour: 23, endHour: 25 };
      case "WHOLE_DAY":
        return { startHour: 7, endHour: 25 };
      default:
        return { startHour: 7, endHour: 25 };
    }
  };

  const getVirtualRealityTimes = () => {
    switch (timeFilter) {
      case "MORNING":
        return { startHour: 8, endHour: 13 };
      case "MIDDAY":
        return { startHour: 13, endHour: 19 };
      case "AFTERNOON":
        return { startHour: 13, endHour: 19 };
      case "EVENING":
        return { startHour: 19, endHour: 25 };
      case "WHOLE_DAY":
        return { startHour: 8, endHour: 25 };
      default:
        return { startHour: 8, endHour: 25 };
    }
  };

  const getBattleKartTimes = () => {
    switch (timeFilter) {
      case "MORNING":
        return { startHour: 7, endHour: 11 };
      case "LUNCH":
        return { startHour: 11, endHour: 15 };
      case "ARVO":
        return { startHour: 15, endHour: 19 };
      case "EVENING":
        return { startHour: 19, endHour: 23 };
      case "LATE":
        return { startHour: 23, endHour: 25 };
      case "WHOLE_DAY":
        return { startHour: 7, endHour: 25 };
      default:
        return { startHour: 7, endHour: 25 };
    }
  };

  switch (track) {
    case "MAIN_TRACK":
      return getMainTrackTimes();
    case "JUNIOR_TRACK":
      return getJuniorTrackTimes();
    case "MINI_TRACK":
      return getMiniTrackTimes();
    case "VIRTUAL_REALITY":
      return getVirtualRealityTimes();
    case "BATTLE_KART_TRACK":
      return getBattleKartTimes();
    default:
      return getMainTrackTimes();
  }
};

export const convertTo12HourFormat = (hour: number) => {
  if (hour < 12) {
    return `${hour}am`;
  } else if (hour === 12) {
    return `${hour}pm`;
  } else if (hour < 24) {
    return `${hour - 12}pm`;
  } else {
    return `${hour - 24}am`;
  }
};

/**
 * Converts a hex color to RGB values
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  };
};

/**
 * Converts RGB values to hex color
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number) => {
    const hex = Math.round(n).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const interpolateColours = (hex1: string, hex2: string, percentage: number): string => {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  
  const r = rgb1.r + (rgb2.r - rgb1.r) * percentage;
  const g = rgb1.g + (rgb2.g - rgb1.g) * percentage;
  const b = rgb1.b + (rgb2.b - rgb1.b) * percentage;
  
  return rgbToHex(r, g, b);
};
